import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/react'

import { FileInfo, uploadFileWithProgress as _uploadFileWithProgress, useGetFileByIdQuery, useLazyGetFileByIdQuery } from '../../store/media/file'
import { useCreateDocumentMutation, useDeleteDocumentMutation, useMarkDocumentFileUploadedMutation } from '../../store/ai/documents'
import { useGetEmailQuery, useLoadEmailAttachmentToDocumentMutation } from '../../store/integrations/integrations'
import { useGetRetentionPoliciesQuery } from '../../store/account/account'
import { selectAccountId } from '../../store/auth/authSlice'
import { useAppSelector } from '../../store/hooks'
import { useListAiAgentResultQuery } from '../../store/ai/aiAgentResult'
import { AIAgentType } from '../../models/AIAgent'

import { RetentionResourceType, RetentionRuleType } from '../../models/RetentionPolicy'

import { type IntegrationServiceId } from '../../common/integrations'
import { useCurrentUserIntegrations } from '../Integration/useCurrentUserIntegrations'
import { type FileBrowserItem } from '../../components/DocumentFileBrowser/types'

import {
  DocumentUploadSource,
  hashFile,
  inferContentType,
  isPdfPasswordProtected,
  MAX_CONCURRENT_FILE_READS,
  MAX_DOCUMENT_BATCH_FILES,
  MAX_DOCUMENT_FILE_SIZE_BYTES,
  MAX_DOCUMENT_FILE_SIZE_BYTES_ZDR,
  MAX_PASSWORD_PROTECTED_DOCUMENT_FILE_SIZE_BYTES,
  runWithConcurrencyLimit,
} from './DocumentUpload/utils'
import { DocumentCreate, DocumentFile, DocumentFileType } from '../../models/Document'
import {
  DocumentConnectorItem,
  useGetDocumentConnectorFileQuery,
  useLazyGetDocumentConnectorFileQuery,
} from '../../store/documentConnectors/documentConnectors'
import { DocumentFileInfo, selectDocument } from '../../store/processDocument/document'
import { skipToken } from '@reduxjs/toolkit/query'

export interface ZocksDocumentUploadQueueItem {
  source: DocumentUploadSource.ZOCKS
  id: string
  file: File
  isUploading: boolean
  isUploaded: boolean
  abortController: AbortController
  documentId?: string
  progress?: number
  hash?: string
  needsPassword?: boolean
  password?: string
}

export interface IntegrationDocumentUploadQueueItem {
  source: DocumentUploadSource.INTEGRATION
  id: string
  serviceId: IntegrationServiceId
  fileId: string
  name: string
  size: number
  documentId?: string
  error?: boolean
}

export interface EmailAttachmentDocumentUploadQueueItem {
  source: DocumentUploadSource.EMAIL_ATTACHMENT
  id: string
  connector: string
  emailId: string
  attachmentId: string
  name: string
  size: number
  documentId?: string
  error?: boolean
}

export type DocumentUploadQueueItem = ZocksDocumentUploadQueueItem | IntegrationDocumentUploadQueueItem | EmailAttachmentDocumentUploadQueueItem

export const emailAttachmentQueueKey = (emailId: string, attachmentId: string): string => `${emailId}:${attachmentId}`

export interface EmailAttachmentToAdd {
  id: string
  connector: string
  emailId: string
  attachmentId: string
  name: string
  size: number
}

const isQueueItemReady = (item: DocumentUploadQueueItem) =>
  item.source === DocumentUploadSource.INTEGRATION || item.source === DocumentUploadSource.EMAIL_ATTACHMENT ? !!item.documentId : item.isUploaded

interface PasswordModalState {
  items: ZocksDocumentUploadQueueItem[]
  initialActiveItemId: string | null
}

export const useDocumentFileUpload = () => {
  const accountId = useAppSelector(selectAccountId)

  const [triggerCreateDocument] = useCreateDocumentMutation()
  const [markDocumentFileUploaded] = useMarkDocumentFileUploadedMutation()
  const [triggerDeleteDocument] = useDeleteDocumentMutation()
  const [triggerLoadEmailAttachmentToDocument] = useLoadEmailAttachmentToDocumentMutation()

  const [uploadQueueItems, setUploadQueueItems] = useState<DocumentUploadQueueItem[]>([])
  const [passwordModal, setPasswordModal] = useState<PasswordModalState | null>(null)

  const clearedItemIdsRef = useRef<Set<string>>(new Set())

  const passwordPendingItems = uploadQueueItems.filter(
    (item): item is ZocksDocumentUploadQueueItem => item.source === DocumentUploadSource.ZOCKS && !!item.needsPassword,
  )

  const openPasswordModal = (state: PasswordModalState) => {
    setPasswordModal(state)
  }

  const closePasswordModal = () => {
    setPasswordModal(null)
  }

  const onProgress = (itemId: string, progress: number) => {
    setUploadQueueItems((prev) => prev.map((item) => (item.source === DocumentUploadSource.ZOCKS && item.id === itemId ? { ...item, progress } : item)))
  }

  const onSuccess = (itemId: string) => {
    setUploadQueueItems((prev) =>
      prev.map((item) => (item.source === DocumentUploadSource.ZOCKS && item.id === itemId ? { ...item, isUploading: false, isUploaded: true } : item)),
    )
  }

  const onError = (itemId: string) => {
    setUploadQueueItems((prev) =>
      prev.map((item) => (item.source === DocumentUploadSource.ZOCKS && item.id === itemId ? { ...item, isUploading: false } : item)),
    )
  }

  const createDraftDocument = async (itemId: string, payload: DocumentCreate) => {
    const { document, fileUploadTargets } = await triggerCreateDocument({ accountId, payload }).unwrap()

    if (clearedItemIdsRef.current.has(itemId)) {
      clearedItemIdsRef.current.delete(itemId)
      await triggerDeleteDocument({ accountId, documentId: document.id })
      return null
    }

    setUploadQueueItems((prev) => prev.map((item) => (item.id === itemId ? { ...item, documentId: document.id } : item)))

    return { documentId: document.id, uploadTarget: fileUploadTargets[0] }
  }

  const createDocumentAndUploadFile = async (itemId: string, file: File, hash: string, signal: AbortSignal, password?: string) => {
    const payload: DocumentCreate = {
      name: file.name,
      files: [{ type: DocumentFileType.ZOCKS, name: file.name, size: file.size, contentType: file.type, hash, password }],
    }

    const draft = await createDraftDocument(itemId, payload)
    if (!draft) {
      return
    }

    const { uploadTarget } = draft
    if (!uploadTarget) {
      onError(itemId)
      return
    }

    await _uploadFileWithProgress(uploadTarget.fileUploadUrl, uploadTarget.fileUploadParameters, file, (progress) => onProgress(itemId, progress), signal)

    await markDocumentFileUploaded({ accountId, documentId: uploadTarget.documentId, fileId: uploadTarget.fileId }).unwrap()

    onSuccess(itemId)
  }

  const onClear = async (itemId: string) => {
    const item = uploadQueueItems.find((item) => item.id === itemId)
    if (!item) {
      return
    }

    setUploadQueueItems((prev) => prev.filter((item) => item.id !== itemId))

    try {
      if (!item.documentId) {
        clearedItemIdsRef.current.add(itemId)
        if (item.source === DocumentUploadSource.ZOCKS) {
          item.abortController.abort()
        }
        return
      }

      if (item.source === DocumentUploadSource.ZOCKS && item.isUploading) {
        item.abortController.abort()
      }

      await triggerDeleteDocument({ accountId, documentId: item.documentId })
    } catch (e) {
      console.error(e)
    }
  }

  const onClearAll = async () => {
    await Promise.all(uploadQueueItems.map((item) => onClear(item.id)))
  }

  const processQueuedFile = async (itemId: string, file: File, abortController: AbortController) => {
    if (clearedItemIdsRef.current.has(itemId)) {
      clearedItemIdsRef.current.delete(itemId)
      return
    }

    try {
      const hash = await hashFile(file)

      setUploadQueueItems((prev) => prev.map((item) => (item.source === DocumentUploadSource.ZOCKS && item.id === itemId ? { ...item, hash } : item)))

      await createDocumentAndUploadFile(itemId, file, hash, abortController.signal)
    } catch (e) {
      console.error(e)
      onError(itemId)
    }
  }

  const onFilesSelected = async (files: File[]): Promise<{ oversizedLockedFiles: File[] }> => {
    const checkedFiles = await runWithConcurrencyLimit(files, MAX_CONCURRENT_FILE_READS, async (file) => ({
      file,
      needsPassword: await isPdfPasswordProtected(file),
    }))

    const allLockedFiles = checkedFiles.filter((checked) => checked.needsPassword).map((checked) => checked.file)
    const unlockedFiles = checkedFiles.filter((checked) => !checked.needsPassword).map((checked) => checked.file)

    const oversizedLockedFiles = allLockedFiles.filter((file) => file.size > MAX_PASSWORD_PROTECTED_DOCUMENT_FILE_SIZE_BYTES)
    const lockedFiles = allLockedFiles.filter((file) => file.size <= MAX_PASSWORD_PROTECTED_DOCUMENT_FILE_SIZE_BYTES)

    if (lockedFiles.length) {
      const newItems: ZocksDocumentUploadQueueItem[] = lockedFiles.map((file) => ({
        source: DocumentUploadSource.ZOCKS,
        id: nanoid(),
        file,
        isUploading: false,
        isUploaded: false,
        abortController: new AbortController(),
        needsPassword: true,
      }))

      setUploadQueueItems((prev) => [...prev, ...newItems])
      openPasswordModal({ items: [...passwordPendingItems, ...newItems], initialActiveItemId: null })
    }

    const queuedUnlockedFiles = unlockedFiles.map((file) => ({ itemId: nanoid(), file, abortController: new AbortController() }))

    if (queuedUnlockedFiles.length) {
      setUploadQueueItems((prev) => [
        ...prev,
        ...queuedUnlockedFiles.map(({ itemId, file, abortController }) => ({
          source: DocumentUploadSource.ZOCKS as const,
          id: itemId,
          isUploading: true,
          isUploaded: false,
          file,
          abortController,
        })),
      ])
    }

    await runWithConcurrencyLimit(queuedUnlockedFiles, MAX_CONCURRENT_FILE_READS, ({ itemId, file, abortController }) =>
      processQueuedFile(itemId, file, abortController),
    )

    return { oversizedLockedFiles }
  }

  const onPasswordProvided = async (itemId: string, password: string) => {
    const item = passwordPendingItems.find((item) => item.id === itemId)
    if (!item) {
      return
    }

    const abortController = new AbortController()
    setUploadQueueItems((prev) =>
      prev.map((i) =>
        i.source === DocumentUploadSource.ZOCKS && i.id === itemId ? { ...i, isUploading: true, needsPassword: false, password, abortController } : i,
      ),
    )

    try {
      const hash = await hashFile(item.file)

      setUploadQueueItems((prev) => prev.map((i) => (i.source === DocumentUploadSource.ZOCKS && i.id === itemId ? { ...i, hash } : i)))

      await createDocumentAndUploadFile(itemId, item.file, hash, abortController.signal, password)
    } catch (e) {
      console.error(e)
      onError(itemId)
    }
  }

  const createIntegrationDocument = async (item: IntegrationDocumentUploadQueueItem) => {
    const payload: DocumentCreate = {
      name: item.name,
      files: [
        {
          type: DocumentFileType.INTEGRATION,
          fileId: item.fileId,
          connector: item.serviceId,
          name: item.name,
          size: item.size,
          contentType: inferContentType(item.name),
        },
      ],
    }

    try {
      await createDraftDocument(item.id, payload)
    } catch (e) {
      console.error(e)
      setUploadQueueItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, error: true } : i)))
    }
  }

  const onIntegrationFilesSelected = async (serviceId: IntegrationServiceId, items: FileBrowserItem[]) => {
    const remaining = MAX_DOCUMENT_BATCH_FILES - uploadQueueItems.length

    const newItems: IntegrationDocumentUploadQueueItem[] = items
      .filter((item) => item.type === 'file')
      .slice(0, Math.max(0, remaining))
      .map((item) => ({ source: DocumentUploadSource.INTEGRATION, id: nanoid(), serviceId, fileId: item.id, name: item.name, size: item.size ?? 0 }))

    setUploadQueueItems((prev) => [...prev, ...newItems])

    await Promise.all(newItems.map(createIntegrationDocument))
  }

  const createEmailAttachmentDocument = async (item: EmailAttachmentDocumentUploadQueueItem): Promise<boolean> => {
    try {
      const document = await triggerLoadEmailAttachmentToDocument({
        accountId,
        connector: item.connector,
        emailExternalId: item.emailId,
        attachmentExternalId: item.attachmentId,
      }).unwrap()

      if (clearedItemIdsRef.current.has(item.id)) {
        clearedItemIdsRef.current.delete(item.id)
        await triggerDeleteDocument({ accountId, documentId: document.id })
        return false
      }

      setUploadQueueItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, documentId: document.id } : i)))
      return true
    } catch (e) {
      console.error(e)
      Sentry.captureException(e)
      setUploadQueueItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, error: true } : i)))
      return false
    }
  }

  const onEmailAttachmentsSelected = async (attachments: EmailAttachmentToAdd[]): Promise<number> => {
    const remaining = MAX_DOCUMENT_BATCH_FILES - uploadQueueItems.length

    const newItems: EmailAttachmentDocumentUploadQueueItem[] = attachments.slice(0, Math.max(0, remaining)).map((attachment) => ({
      source: DocumentUploadSource.EMAIL_ATTACHMENT,
      id: attachment.id,
      connector: attachment.connector,
      emailId: attachment.emailId,
      attachmentId: attachment.attachmentId,
      name: attachment.name,
      size: attachment.size,
    }))

    setUploadQueueItems((prev) => [...prev, ...newItems])

    const results = await Promise.all(newItems.map(createEmailAttachmentDocument))
    return results.filter(Boolean).length
  }

  const onRetry = async (itemId: string) => {
    const item = uploadQueueItems.find((item) => item.id === itemId)
    if (!item) {
      return
    }

    if (item.source === DocumentUploadSource.INTEGRATION) {
      setUploadQueueItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, error: false } : i)))
      await createIntegrationDocument(item)
      return
    }

    if (item.source === DocumentUploadSource.EMAIL_ATTACHMENT) {
      setUploadQueueItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, error: false } : i)))
      await createEmailAttachmentDocument(item)
      return
    }

    if (item.source !== DocumentUploadSource.ZOCKS || !item.hash) {
      return
    }

    const abortController = new AbortController()
    setUploadQueueItems((prev) =>
      prev.map((item) =>
        item.source === DocumentUploadSource.ZOCKS && item.id === itemId
          ? { ...item, isUploading: true, isUploaded: false, progress: 0, documentId: undefined, abortController }
          : item,
      ),
    )

    if (item.documentId) {
      try {
        await triggerDeleteDocument({ accountId, documentId: item.documentId })
      } catch (e) {
        console.error(e)
      }
    }

    try {
      await createDocumentAndUploadFile(itemId, item.file, item.hash, abortController.signal, item.password)
    } catch (e) {
      console.error(e)
      onError(itemId)
    }
  }

  const readyCount = uploadQueueItems.filter(isQueueItemReady).length
  const allItemsReady = uploadQueueItems.length > 0 && readyCount === uploadQueueItems.length

  return {
    onFilesSelected,
    onIntegrationFilesSelected,
    onEmailAttachmentsSelected,
    uploadQueueItems,
    readyCount,
    allItemsReady,
    onClear,
    onClearAll,
    onRetry,
    passwordModal,
    passwordPendingItems,
    openPasswordModal,
    closePasswordModal,
    onPasswordProvided,
  }
}

export const useSafeTimeout = () => {
  const ref = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearSafeTimeout = () => {
    if (ref.current) {
      clearTimeout(ref.current)
    }
  }

  useEffect(() => {
    return () => {
      clearSafeTimeout()
    }
  }, [])

  const setSafeTimeout = (callback: () => void, delay?: number) => {
    ref.current = setTimeout(callback, delay)
  }

  return { setSafeTimeout, clearSafeTimeout }
}

export const useProcessDocumentFileInput = (supportedMimeTypes: string[], onFilesSelected: (files: File[]) => Promise<void>, disabled = false) => {
  const [isHovering, setIsHovering] = useState<boolean>(false)

  useEffect(() => {
    const handleDrop = (e: DragEvent) => {
      e.preventDefault()
    }

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault()
    }

    window.addEventListener('drop', handleDrop)
    window.addEventListener('dragover', handleDragOver)

    return () => {
      window.removeEventListener('drop', handleDrop)
      window.removeEventListener('dragover', handleDragOver)
    }
  }, [])

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()

    if (disabled) {
      return
    }

    const input = e.target
    const { files } = input
    if (!files?.length) {
      return
    }

    const filteredFiles = Array.from(files).filter((file) => supportedMimeTypes.includes(file.type))

    input.value = ''

    if (!filteredFiles.length) {
      return
    }

    await onFilesSelected(filteredFiles)
  }

  const handleDrop = async (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()

    setIsHovering(false)

    if (disabled) {
      return
    }

    const { dataTransfer } = e
    const { files } = dataTransfer
    if (!files.length) {
      return
    }

    const filteredFiles = Array.from(files).filter((file) => supportedMimeTypes.includes(file.type))
    if (!filteredFiles.length) {
      return
    }

    await onFilesSelected(filteredFiles)
  }

  const handleDragEnter = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (disabled) {
      return
    }

    setIsHovering(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsHovering(false)
  }

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  return {
    isHovering,
    handleChange,
    handleDrop,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
  }
}

const connectorItemToDocumentFileInfo = (item: DocumentConnectorItem): DocumentFileInfo => ({
  fileId: item.integration?.externalId ?? item.id ?? '',
  fileName: item.name,
  fileSize: item.size ?? 0,
  contentType: item.contentType ?? '',
})

const isEmailAttachmentDocumentFile = (documentFile?: DocumentFile): documentFile is Extract<DocumentFile, { type: DocumentFileType.EMAIL_ATTACHMENT }> =>
  documentFile?.type === DocumentFileType.EMAIL_ATTACHMENT

const documentFileToDocumentFileInfo = (documentFile: DocumentFile): DocumentFileInfo => ({
  fileId: isEmailAttachmentDocumentFile(documentFile) ? `${documentFile.emailId}:${documentFile.attachmentId}` : documentFile.fileId,
  fileName: documentFile.name,
  fileSize: documentFile.size,
  contentType: documentFile.contentType,
})

export const useDocumentFile = (documentFilesToInitialize?: DocumentFile[]) => {
  const accountId = useAppSelector(selectAccountId)

  const [triggerGetFileById] = useLazyGetFileByIdQuery()
  const [triggerGetDocumentFileById] = useLazyGetDocumentConnectorFileQuery()

  const documentFile = documentFilesToInitialize?.[0]

  const { documentUploadSupportedIntegrations, isLoading: isCurrentUserIntegrationsLoading } = useCurrentUserIntegrations()

  const documentUploadSupportedIntegrationServiceIds = useMemo(
    () => documentUploadSupportedIntegrations.map(({ serviceId }) => serviceId),
    [documentUploadSupportedIntegrations],
  )

  const { data: zocksFileInfo, isFetching: zocksFetching } = useGetFileByIdQuery(
    documentFile?.type === DocumentFileType.ZOCKS ? { accountId, fileId: documentFile.fileId } : skipToken,
  )
  const { data: integrationFileInfo, isFetching: integrationFetching } = useGetDocumentConnectorFileQuery(
    documentFile?.type === DocumentFileType.INTEGRATION && documentUploadSupportedIntegrationServiceIds.includes(documentFile.connector as IntegrationServiceId)
      ? { accountId, connector: documentFile.connector, fileId: documentFile.fileId }
      : skipToken,
  )

  const fetchZocksDocumentFile = useCallback(
    async (fileId: string): Promise<FileInfo | null> => {
      try {
        return await triggerGetFileById({ accountId, fileId }).unwrap()
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, triggerGetFileById],
  )

  const fetchIntegrationDocumentFile = useCallback(
    async (connector: string, fileId: string): Promise<DocumentFileInfo | null> => {
      try {
        const item = await triggerGetDocumentFileById({ accountId, connector, fileId }).unwrap()
        return item ? connectorItemToDocumentFileInfo(item) : null
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, triggerGetDocumentFileById],
  )

  const fetchDocumentFile = useCallback(
    async (documentFile: DocumentFile): Promise<FileInfo | DocumentFileInfo | null> => {
      if (isEmailAttachmentDocumentFile(documentFile)) {
        return documentFileToDocumentFileInfo(documentFile)
      }
      if (documentFile.type === DocumentFileType.INTEGRATION) {
        if (!documentUploadSupportedIntegrationServiceIds.includes(documentFile.connector as IntegrationServiceId)) {
          return null
        }
        return await fetchIntegrationDocumentFile(documentFile.connector, documentFile.fileId)
      }
      return await fetchZocksDocumentFile(documentFile.fileId)
    },
    [documentUploadSupportedIntegrationServiceIds, fetchIntegrationDocumentFile, fetchZocksDocumentFile],
  )

  const fileInfo = isEmailAttachmentDocumentFile(documentFile)
    ? documentFileToDocumentFileInfo(documentFile)
    : documentFile?.type === DocumentFileType.INTEGRATION
      ? integrationFileInfo
        ? connectorItemToDocumentFileInfo(integrationFileInfo)
        : integrationFileInfo
      : zocksFileInfo

  return { fetchDocumentFile, isLoading: integrationFetching || zocksFetching || isCurrentUserIntegrationsLoading, fileInfo }
}

export const useEmailAttachmentThreadLink = (documentFile?: DocumentFile) => {
  const accountId = useAppSelector(selectAccountId)

  const { data: email, isFetching } = useGetEmailQuery(
    isEmailAttachmentDocumentFile(documentFile) ? { accountId, emailConnector: documentFile.connector, emailId: documentFile.emailId } : skipToken,
  )

  return {
    threadLink: email ? `/email/inbox/threads/${email.threadId}` : null,
    isLoading: isEmailAttachmentDocumentFile(documentFile) && isFetching,
  }
}

export const useDocumentSummaryAiAgentResult = () => {
  const accountId = useAppSelector(selectAccountId)
  const document = useAppSelector(selectDocument)

  const { currentData: aiAgentResults, refetch } = useListAiAgentResultQuery(
    document ? { accountId, type: AIAgentType.DOCUMENT_SUMMARY, resultKey: document.id } : skipToken,
  )

  const refetchAiAgentResult = useCallback(async () => {
    try {
      const aiAgentResults = await refetch().unwrap()
      if (!aiAgentResults.length) {
        return null
      }
      return aiAgentResults[0]
    } catch (e) {
      console.error(e)
      return null
    }
  }, [refetch])

  return { aiAgentResult: aiAgentResults?.length ? aiAgentResults[0] : null, refetchAiAgentResult }
}

export const useAccountHasDocumentZdrPolicy = () => {
  const accountId = useAppSelector(selectAccountId)

  const { data: retentionPoliciesResponse } = useGetRetentionPoliciesQuery({ accountId })

  return useMemo(
    () =>
      !!retentionPoliciesResponse?.data.find(({ resource, rule }) => resource.type === RetentionResourceType.DOCUMENT && rule.type === RetentionRuleType.ZERO),
    [retentionPoliciesResponse],
  )
}

export const useMaxDocumentFileSizeBytes = () => {
  const accountHasZDRPolicy = useAccountHasDocumentZdrPolicy()

  return { maxFileSizeBytes: accountHasZDRPolicy ? MAX_DOCUMENT_FILE_SIZE_BYTES_ZDR : MAX_DOCUMENT_FILE_SIZE_BYTES, accountHasZDRPolicy }
}
